import styles from './styles.module.css'
import { IAuditImage } from '../../../../global/types/audit'
import { useState } from 'react'
import FullScreenImage from './FullScreenImage'
import NavigationButton from '../../../../global/components/buttons/navigation-button/NavigationButton'
import {
  REQUIRED_DVIR_IMAGE_BY_TYPE,
  REQUIRED_INSTALLATION_IMAGE_BY_TYPE,
} from '../../../../global/constants/image'
interface ImagesDisplayProps {
  imageTypes: string[]
  technicianImages: IAuditImage[]
  exampleImages: IAuditImage[]
  currentImageIndex: number
  setCurrentImageIndex: Function
}

function isImageRequired(imageType: string): boolean {
  const installationImageRequired =
    REQUIRED_INSTALLATION_IMAGE_BY_TYPE.includes(imageType)
  const dvirImageRequired = REQUIRED_DVIR_IMAGE_BY_TYPE.includes(imageType)
  return installationImageRequired || dvirImageRequired
}

function ImagesDisplay({
  imageTypes,
  technicianImages,
  exampleImages,
  currentImageIndex,
  setCurrentImageIndex,
}: ImagesDisplayProps) {
  const [isFullScreenVisible, setFullScreenVisible] = useState(false)
  const imagesByType: {
    exampleImage: IAuditImage | undefined
    technicianImage: IAuditImage | undefined
  }[] = imageTypes
    .map((type) => {
      const initialTechnicianImage: IAuditImage = {
        url: '',
        type,
      }
      return {
        exampleImage: exampleImages.find((image) => image.type === type),
        technicianImage: {
          //by spreading the initial image, we can handle the case of when a tech image is required but not provided
          ...initialTechnicianImage,
          ...technicianImages.find((image) => image.type === type),
        },
      }
    })
    .filter(({ technicianImage }) => {
      // Filter out images that are not required and are missing
      const existsTechnicianImage = Boolean(technicianImage?.url)
      const isTechnicianImageRequired = isImageRequired(
        technicianImage?.type ?? '',
      )
      return existsTechnicianImage || isTechnicianImageRequired
    })

  const isNoImageToRender = imagesByType.every((imageType) => {
    const isNoExampleImage =
      !imageType.exampleImage || !imageType.exampleImage.url
    const isNoTechnicianImage =
      !imageType.technicianImage || !imageType.technicianImage.url
    return isNoExampleImage && isNoTechnicianImage
  })

  if (isNoImageToRender) {
    return <div className={styles.imageDisplayContainer}></div>
  }

  const isOneImageToRender = imagesByType.length === 1

  if (isOneImageToRender) {
    const exampleImageToRender = imagesByType[0].exampleImage
    const technicianImageToRender = imagesByType[0].technicianImage
    const existsTechnicianImageToRender = Boolean(technicianImageToRender?.url)
    const existsEcamplesImageToRender = Boolean(exampleImageToRender?.url)

    return (
      <div
        className={
          exampleImageToRender
            ? styles.imageDisplayContainer
            : styles.imageDisplayContainerWithoutExamples
        }
      >
        {existsEcamplesImageToRender && (
          <div className={styles.exampleContainer}>
            <div>
              <span style={{ fontWeight: 'bold' }}>EXAMPLE: </span>
              {exampleImageToRender?.type}
            </div>
            <img
              src={exampleImageToRender?.url}
              alt={exampleImageToRender?.type}
            />
          </div>
        )}
        <>
          <FullScreenImage
            image={technicianImageToRender ?? ({} as IAuditImage)}
            fullscreenVisible={isFullScreenVisible}
            setFullScreenVisible={setFullScreenVisible}
          />
          <div
            className={
              exampleImageToRender
                ? styles.contentContainer
                : styles.contentContainerWithoutExamples
            }
          >
            <div className={styles.imageContainer}>
              <div>{technicianImageToRender?.type}</div>
              {existsTechnicianImageToRender ? (
                <img
                  src={technicianImageToRender?.url}
                  alt={technicianImageToRender?.type}
                  onClick={() => setFullScreenVisible(true)}
                />
              ) : (
                <div className={styles.placeholderBox}>
                  <div className={styles.placeholderCross}></div>
                </div>
              )}
            </div>
          </div>
        </>
      </div>
    )
  }

  function handleClick(input: 'prev' | 'next') {
    const shouldSubtractOne = input === 'prev'
    const addend = shouldSubtractOne ? -1 : 1
    const length = imagesByType.length
    const nextIndex = (currentImageIndex + addend + length) % length
    setCurrentImageIndex(nextIndex)
  }

  const existsTechnicianImageToRender = Boolean(
    imagesByType[currentImageIndex].technicianImage?.url,
  )
  const existsExampleImageToRender = Boolean(
    imagesByType[currentImageIndex].exampleImage?.url,
  )

  return (
    <div
      className={
        imagesByType[currentImageIndex].exampleImage
          ? styles.imageDisplayContainer
          : styles.imageDisplayContainerWithoutExamples
      }
    >
      {existsExampleImageToRender && (
        <div className={styles.exampleContainer}>
          <div>
            <span style={{ fontWeight: 'bold' }}>EXAMPLE: </span>
            {imagesByType[currentImageIndex].exampleImage?.type}
          </div>
          <img
            src={imagesByType[currentImageIndex].exampleImage?.url}
            alt={imagesByType[currentImageIndex].exampleImage?.type}
          />
        </div>
      )}
      <div
        className={
          imagesByType[currentImageIndex].exampleImage
            ? styles.contentContainer
            : styles.contentContainerWithoutExamples
        }
      >
        <div className={styles.navigationButtons}>
          <NavigationButton
            direction='back'
            handleClick={() => handleClick('prev')}
          />
        </div>
        <>
          <FullScreenImage
            image={
              imagesByType[currentImageIndex].technicianImage ??
              ({} as IAuditImage)
            }
            fullscreenVisible={isFullScreenVisible}
            setFullScreenVisible={setFullScreenVisible}
          />
          <div className={styles.imageContainer}>
            <div>{imagesByType[currentImageIndex].technicianImage?.type}</div>
            {existsTechnicianImageToRender ? (
              <img
                src={imagesByType[currentImageIndex].technicianImage?.url}
                alt={imagesByType[currentImageIndex].technicianImage?.type}
                onClick={() => setFullScreenVisible(true)}
              />
            ) : (
              <div className={styles.placeholderBox}>
                <div className={styles.placeholderCross}></div>
              </div>
            )}
          </div>
        </>
        <div className={styles.navigationButtons}>
          <NavigationButton
            direction='forward'
            handleClick={() => handleClick('next')}
          />
        </div>
      </div>
      <div
        className={
          imagesByType[currentImageIndex].exampleImage
            ? styles.thumbnailContainer
            : styles.thumbnailContainerWithoutExamples
        }
      >
        {imagesByType.map((images, index) => {
          const isActive = index === currentImageIndex
          const doesImageExist = Boolean(images.technicianImage?.url)
          if (doesImageExist) {
            return (
              <img
                className={`${styles.thumbnail} ${
                  isActive ? styles.thumbnailActive : ''
                }`}
                key={index}
                src={images.technicianImage?.url}
                alt={images.technicianImage?.type}
                onClick={() => setCurrentImageIndex(index)}
              />
            )
          } else {
            return (
              <div
                key={index}
                className={`${styles.thumbnailPlaceholder} ${
                  isActive ? styles.thumbnailActive : ''
                }`}
                onClick={() => setCurrentImageIndex(index)}
              ></div>
            )
          }
        })}
      </div>
    </div>
  )
}

export default ImagesDisplay
